import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, MapPin, ArrowUpRight, Star } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";

export default function LocalPage() {
  const { slug } = useParams();
  const { localPages, services, zones, settings, reviews, loading } = useData();

  if (loading) return <div className="min-h-screen bg-av-black" />;
  const p = localPages.find((x) => x.slug === slug);
  if (!p) return <Navigate to="/zone-servite" replace />;

  const featured = (p.servizi || []).map((t) => services.find((s) => s.titolo === t)).filter(Boolean);
  const list = featured.length ? featured : services.slice(0, 3);
  const nearby = zones.filter((z) => z.slug !== slug && z.slug !== "comuni-limitrofi").slice(0, 7);
  const sampleReviews = (reviews || []).slice(0, 3);
  const paragraphs = (p.intro || "").split("\n\n").filter(Boolean);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        name: "Animaverde",
        image: "https://www.giardinianimaverde.it/logo-animaverde.png",
        telephone: settings?.telefono_link || "+393489904574",
        email: settings?.email,
        url: `https://www.giardinianimaverde.it/giardiniere/${p.slug}`,
        priceRange: "€€",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Via Garibaldi 143/1",
          addressLocality: "Ferno",
          addressRegion: "VA",
          postalCode: "21010",
          addressCountry: "IT",
        },
        areaServed: { "@type": "City", name: p.comune },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://www.giardinianimaverde.it/" },
          { "@type": "ListItem", position: 2, name: "Zone servite", item: "https://www.giardinianimaverde.it/zone-servite" },
          { "@type": "ListItem", position: 3, name: p.comune, item: `https://www.giardinianimaverde.it/giardiniere/${p.slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <Seo title={p.meta_title || `${p.hero_titolo}`} description={p.meta_description} path={`/giardiniere/${p.slug}`} schema={schema} />
      <PageHero
        overline={`Cura del verde a ${p.comune}`}
        title={p.hero_titolo}
        subtitle={paragraphs[0]}
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <Link to="/zone-servite" className="hover:text-av-lime">Zone servite</Link> / <span className="text-av-white">{p.comune}</span></>}
      />

      <section className="bg-av-black py-14 sm:py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-12">
            {/* Content */}
            <div>
              <Reveal>
                {paragraphs.slice(1).map((par, i) => (
                  <p key={i} className="text-av-white/85 text-base sm:text-lg leading-relaxed mb-5">{par}</p>
                ))}

                {/* Highlights */}
                {(p.highlights || []).length > 0 && (
                  <ul className="mt-6 space-y-3">
                    {p.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3 text-av-white/90">
                        <Check size={18} className="text-av-lime mt-0.5 shrink-0" /> <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>

              {/* Featured services */}
              <h2 className="font-display text-3xl text-av-white mt-14 mb-6">I nostri servizi a {p.comune}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {list.map((s, i) => (
                  <Reveal key={s.id} delay={(i % 2) * 0.06}>
                    <Link to={`/servizi#${s.slug}`} data-testid={`local-service-${s.slug}`} className="group block rounded-2xl border border-white/10 p-6 hover:border-av-lime/50 transition-colors bg-av-green-dark/20" style={{ backgroundColor: "rgba(22,56,29,0.2)" }}>
                      <h3 className="font-display text-xl text-av-white group-hover:text-av-lime transition-colors">{s.titolo}</h3>
                      <p className="mt-2 text-sm text-av-gray line-clamp-3">{s.breve}</p>
                      <span className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-av-lime">Scopri di più <ArrowUpRight size={14} /></span>
                    </Link>
                  </Reveal>
                ))}
              </div>

              {/* Reviews */}
              {sampleReviews.length > 0 && (
                <div className="mt-14">
                  <h2 className="font-display text-3xl text-av-white mb-6">Cosa dicono i nostri clienti</h2>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {sampleReviews.map((r) => (
                      <div key={r.id} className="rounded-2xl border border-white/10 p-5 bg-av-green-dark/20" style={{ backgroundColor: "rgba(22,56,29,0.2)" }}>
                        <div className="flex gap-0.5 mb-2">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className="text-av-bright fill-av-bright" />)}</div>
                        <p className="text-sm text-av-white/85 line-clamp-5">{r.testo}</p>
                        <p className="mt-3 text-xs text-av-gray">{r.autore}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar CTA */}
            <aside className="lg:sticky lg:top-24 h-fit">
              <div className="rounded-3xl border border-av-lime/30 bg-av-green-dark/40 p-7" style={{ backgroundColor: "rgba(22,56,29,0.4)" }}>
                <p className="overline text-av-lime mb-2">Sopralluogo gratuito</p>
                <h2 className="font-display text-2xl text-av-white">Richiedi un intervento a {p.comune}</h2>
                <p className="mt-3 text-sm text-av-gray">Raccontaci di cosa hai bisogno: valutiamo insieme la soluzione più adatta, senza impegno.</p>
                <div className="mt-6 flex flex-col gap-3">
                  <Link to="/contatti" data-testid="local-cta-contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-3 text-sm font-semibold hover:bg-av-lime transition-colors">Richiedi un preventivo</Link>
                  <WhatsAppButton variant="outline" className="w-full justify-center" />
                  {settings && <a href={`tel:${settings.telefono_link}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-av-white px-6 py-3 text-sm font-semibold hover:border-av-lime hover:text-av-lime transition-colors">Chiama il {settings.telefono}</a>}
                </div>
              </div>

              {nearby.length > 0 && (
                <div className="mt-6 rounded-3xl border border-white/10 p-7">
                  <h2 className="font-display text-xl text-av-white mb-4">Operiamo anche a</h2>
                  <div className="flex flex-wrap gap-2">
                    {nearby.map((z) => {
                      const has = localPages.some((lp) => lp.slug === z.slug);
                      return has ? (
                        <Link key={z.id} to={`/giardiniere/${z.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-av-white/85 hover:border-av-lime hover:text-av-lime transition-colors">
                          <MapPin size={12} /> {z.nome}
                        </Link>
                      ) : (
                        <span key={z.id} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-av-gray">
                          <MapPin size={12} /> {z.nome}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
